import { Lock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * Mostra um "cartão de cadeado" quando a feature não está disponível no plano.
 * Uso:
 *   <FeatureGate feature="comparison">
 *     <ComparePage />
 *   </FeatureGate>
 */
export default function FeatureGate({ feature, children, fallback }) {
  const { user } = useAuth();
  // Admin tem acesso total a todas as funcionalidades (bypass Stripe)
  if (user?.role === "admin") return children;
  const allowed = !!user?.plan_features?.[feature];
  if (allowed) return children;
  if (fallback) return fallback;
  return <PaywallCard feature={feature} />;
}

export function PaywallCard({ feature }) {
  const names = {
    comparison: "Comparação entre avaliações",
    library_edit: "Biblioteca Funcional completa",
    visceral: "Observações pré-viscerais",
    pdf_whitelabel: "Relatório PDF com branding próprio",
  };
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-center" data-testid="paywall-card">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">
        <Lock className="w-5 h-5" />
      </div>
      <div className="font-display text-xl text-slate-50 mt-3">{names[feature] || "Funcionalidade premium"}</div>
      <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
        Esta funcionalidade faz parte de um plano superior. Atualiza para desbloquear.
      </p>
      <Link
        to="/settings?tab=billing"
        data-testid="paywall-upgrade-link"
        className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold text-sm"
      >
        Atualizar plano <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

/** Hook simples para saber se o utilizador tem a feature. */
export function usePlan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return {
    plan: user?.effective_plan || user?.plan || "base",
    isAdmin,
    trialActive: !!user?.trial_active,
    has: (f) => isAdmin || !!user?.plan_features?.[f],
  };
}
